'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { COMPANY } from '@/lib/constants';
import { formatPriceES, formatNumberES } from '@/lib/format';

type Range = '24h' | '7d' | '30d' | '1y';
type Granularity = 'intraday' | 'daily';

interface SerialPoint {
  t: number;
  iso: string;
  usdPerOz: number;
  eurUsdRate: number;
  eurPerOz: number;
  eurPerGram: number;
}

interface Props {
  initialRange: Range;
  initialGranularity: Granularity;
  initialPoints: SerialPoint[];
  initialLatest: SerialPoint;
  initialChangePct: number | null;
  initialChangeAbs: number | null;
  initialMarketClosed?: boolean;
}

const RANGES: { id: Range; label: string }[] = [
  { id: '24h', label: '24 h' },
  { id: '7d', label: '7 d' },
  { id: '30d', label: '30 d' },
  { id: '1y', label: '1 año' },
];

const MONTHS_ES_SHORT = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

/** "1 may. 2026" deterministic. */
function formatDateLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS_ES_SHORT[Number(m[2]) - 1]}. ${m[1]}`;
}

/** "1 may" o "1 may '26" según rango. */
function formatDateAxis(iso: string, withYear: boolean): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const day = Number(m[3]);
  const month = MONTHS_ES_SHORT[Number(m[2]) - 1];
  return withYear ? `${month} '${m[1]!.slice(-2)}` : `${day} ${month}`;
}

/** "14:30" hora Madrid usando Intl (sólo cliente). */
function formatTimeMadrid(t: number): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(t));
  } catch {
    return new Date(t).toISOString().slice(11, 16);
  }
}

interface ChartGeometry {
  padL: number;
  padR: number;
  padT: number;
  padB: number;
  width: number;
  height: number;
  yMin: number;
  yMax: number;
  xs: number[]; // posición SVG de cada punto
  ys: number[];
}

function computeGeometry(values: number[]): ChartGeometry {
  const padL = 11;
  const padR = 3;
  const padT = 5;
  const padB = 12;
  const width = 100;
  const height = 100;
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Padding del eje Y: 5% del rango (mín 0.1 si todo es plano).
  const range = Math.max(max - min, max * 0.0005);
  const yPad = range * 0.18;
  const yMin = min - yPad;
  const yMax = max + yPad;

  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const n = values.length;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < n; i++) {
    const xRatio = n <= 1 ? 0.5 : i / (n - 1);
    xs.push(padL + xRatio * plotW);
    const v = values[i]!;
    const yRatio = (v - yMin) / (yMax - yMin);
    ys.push(padT + (1 - yRatio) * plotH);
  }
  return { padL, padR, padT, padB, width, height, yMin, yMax, xs, ys };
}

export function GoldPriceWidgetView({
  initialRange,
  initialGranularity,
  initialPoints,
  initialLatest,
  initialChangePct,
  initialChangeAbs,
  initialMarketClosed,
}: Props) {
  const [range, setRange] = useState<Range>(initialRange);
  const [granularity, setGranularity] = useState<Granularity>(initialGranularity);
  const [points, setPoints] = useState<SerialPoint[]>(initialPoints);
  const [latest, setLatest] = useState<SerialPoint>(initialLatest);
  const [changePct, setChangePct] = useState<number | null>(initialChangePct);
  const [changeAbs, setChangeAbs] = useState<number | null>(initialChangeAbs);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [marketClosed, setMarketClosed] = useState(initialMarketClosed ?? false);

  const cacheRef = useRef<Map<Range, {
    granularity: Granularity;
    points: SerialPoint[];
    latest: SerialPoint;
    changePct: number | null;
    changeAbs: number | null;
    marketClosed: boolean;
  }>>(new Map());

  useEffect(() => {
    cacheRef.current.set(initialRange, {
      granularity: initialGranularity,
      points: initialPoints,
      latest: initialLatest,
      changePct: initialChangePct,
      changeAbs: initialChangeAbs,
      marketClosed: initialMarketClosed ?? false,
    });
  }, [initialRange, initialGranularity, initialPoints, initialLatest, initialChangePct, initialChangeAbs, initialMarketClosed]);

  async function selectRange(r: Range) {
    if (r === range) return;
    setRange(r);
    setHover(null);
    setError(null);
    const cached = cacheRef.current.get(r);
    if (cached) {
      setGranularity(cached.granularity);
      setPoints(cached.points);
      setLatest(cached.latest);
      setChangePct(cached.changePct);
      setChangeAbs(cached.changeAbs);
      setMarketClosed(cached.marketClosed);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/precio-oro?range=${r}`, {
        cache: 'force-cache',
      });
      const json = await res.json();
      if (!json.ok) {
        setError(json.error ?? 'no se pudo cargar el rango');
        return;
      }
      const mc = !!(json.marketClosed);
      const next = {
        granularity: json.granularity as Granularity,
        points: json.points as SerialPoint[],
        latest: json.latest as SerialPoint,
        changePct: json.changePct as number | null,
        changeAbs: json.changeAbs as number | null,
        marketClosed: mc,
      };
      cacheRef.current.set(r, next);
      setGranularity(next.granularity);
      setPoints(next.points);
      setLatest(next.latest);
      setChangePct(next.changePct);
      setChangeAbs(next.changeAbs);
      setMarketClosed(mc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'error al cargar');
    } finally {
      setLoading(false);
    }
  }

  // Geometría del gráfico: dominio Y ajustado al rango real (ESTO arregla el
  // problema de "no se ven las variaciones": antes el dominio incluia 0).
  const geom = useMemo(() => {
    const values = points.map((p) => p.eurPerGram);
    if (values.length === 0) {
      return computeGeometry([latest.eurPerGram]);
    }
    return computeGeometry(values);
  }, [points, latest.eurPerGram]);

  const yTicks = useMemo(() => {
    const { yMin, yMax } = geom;
    return [yMax, (yMin + yMax) / 2, yMin];
  }, [geom]);

  const xTicks = useMemo(() => {
    if (points.length === 0) return [] as { x: number; label: string }[];
    const n = points.length;
    const idx = n <= 1 ? [0] : [0, Math.floor(n / 2), n - 1];
    return idx.map((i) => ({
      x: geom.xs[i]!,
      label: labelForPoint(points[i]!, range, granularity),
    }));
  }, [geom.xs, points, range, granularity]);

  const linePath = useMemo(() => {
    if (geom.xs.length === 0) return '';
    return geom.xs
      .map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${geom.ys[i]!.toFixed(2)}`)
      .join(' ');
  }, [geom]);

  const areaPath = useMemo(() => {
    if (geom.xs.length === 0) return '';
    const baseY = (geom.height - geom.padB).toFixed(2);
    const first = `${geom.padL.toFixed(2)},${baseY}`;
    const lineSegs = geom.xs
      .map((x, i) => `${x.toFixed(2)},${geom.ys[i]!.toFixed(2)}`)
      .join(' ');
    const last = `${(geom.width - geom.padR).toFixed(2)},${baseY}`;
    return `${first} ${lineSegs} ${last}`;
  }, [geom]);

  const isUp =
    changePct == null ? true : changePct >= 0;
  const ChangeIcon = isUp ? TrendingUp : TrendingDown;

  // Datos del punto activo (hover) o último punto.
  const focusIndex = hover ?? points.length - 1;
  const focus = points[focusIndex] ?? latest;

  // Etiqueta del cambio: para 24h "vs. 24h"; para diarios "vs. inicio del periodo".
  const changeLabel =
    changePct == null
      ? '—'
      : `${isUp ? '+' : ''}${formatNumberES(Number(changePct.toFixed(2)))}%`;

  const changeAbsLabel =
    changeAbs == null
      ? null
      : `${isUp ? '+' : ''}${formatNumberES(Number(changeAbs.toFixed(2)))} €`;

  function handleMove(e: React.PointerEvent<SVGSVGElement>) {
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const local = pt.matrixTransform(ctm.inverse());
    // Buscar el punto cuya x esté más cerca.
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < geom.xs.length; i++) {
      const d = Math.abs(geom.xs[i]! - local.x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHover(best);
  }

  return (
    <section className="container-page py-20 sm:py-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        className="grid gap-8 rounded-3xl border border-gold-500/25 bg-gradient-to-br from-navy-900/80 via-navy-900/60 to-navy-800/40 p-6 backdrop-blur-xl sm:p-10 lg:grid-cols-12"
      >
        <div className="space-y-5 lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-300">
            Precio orientativo del oro
          </p>
          <h2 className="heading-display text-3xl text-cream sm:text-4xl">
            Conoce el <span className="text-gold-gradient">precio</span> antes
            de comprar
          </h2>
          <p className="text-sm leading-relaxed text-cream/70">
            El oro cotiza globalmente en el mercado mayorista (LBMA, COMEX). El
            precio que ves abajo es la cotización spot, orientativa. La compra
            de oro físico se realiza en{' '}
            <a
              href={COMPANY.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-300 underline-offset-4 hover:underline"
            >
              {COMPANY.name}
            </a>
            .
          </p>
          <div className="rounded-2xl border border-white/10 bg-navy-950/60 p-5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs uppercase tracking-wider text-cream/55">
                EUR / gramo
              </span>
              <span
                className={`flex items-center gap-1 text-xs ${
                  isUp ? 'text-success' : 'text-red-300'
                }`}
                title={`Variación en el rango ${range}`}
              >
                <ChangeIcon className="h-3.5 w-3.5" /> {changeLabel}
                {changeAbsLabel && (
                  <span className="text-cream/45">({changeAbsLabel})</span>
                )}
              </span>
            </div>
            <div className="mt-2 font-mono text-3xl font-semibold text-gold-300 sm:text-4xl">
              {formatPriceES(focus.eurPerGram)}
            </div>
            <div className="mt-1 text-sm text-cream/55">
              Onza troy:{' '}
              <span className="font-mono text-cream/80">
                {formatPriceES(focus.eurPerOz)}
              </span>
              {' · '}
              <span className="font-mono text-cream/55">
                ${formatNumberES(Number(focus.usdPerOz.toFixed(2)))} USD/oz
              </span>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-wider text-cream/40">
              {hover != null
                ? labelForPointDetailed(focus, granularity)
                : `Último: ${labelForPointDetailed(focus, granularity)}`}
              {marketClosed && range === '24h' && (
                <span className="ml-1.5 text-amber-400/70"> · Mercado cerrado</span>
              )}
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {/* Selector de rango */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <div
              role="tablist"
              aria-label="Rango temporal"
              className="inline-flex items-center gap-1 rounded-xl border border-white/10 bg-navy-950/60 p-1"
            >
              {RANGES.map((r) => {
                const active = r.id === range;
                return (
                  <button
                    key={r.id}
                    role="tab"
                    aria-selected={active}
                    type="button"
                    onClick={() => selectRange(r.id)}
                    disabled={loading}
                    className={`min-w-[3.25rem] rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                      active
                        ? 'bg-gold-500/20 text-gold-200'
                        : 'text-cream/60 hover:text-cream'
                    } disabled:opacity-50`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-cream/40">
              {loading
                ? 'cargando…'
                : error
                  ? <span className="text-red-300">{error}</span>
                  : `${points.length} ${granularity === 'intraday' ? 'puntos' : 'días'} · EUR/g${marketClosed && range === '24h' ? ' · cierre' : ''}`}
            </div>
          </div>

          <div
            className="relative h-72 w-full sm:h-80"
            onPointerLeave={() => setHover(null)}
          >
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full touch-none"
              onPointerMove={handleMove}
              onPointerDown={handleMove}
            >
              <defs>
                <linearGradient id="priceArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A961" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#C9A961" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="priceLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#9C7F3F" />
                  <stop offset="50%" stopColor="#FFD86B" />
                  <stop offset="100%" stopColor="#C9A961" />
                </linearGradient>
              </defs>

              {/* Gridlines horizontales en los y-ticks */}
              {yTicks.map((v, i) => {
                const yRatio = (v - geom.yMin) / (geom.yMax - geom.yMin);
                const y =
                  geom.padT + (1 - yRatio) * (geom.height - geom.padT - geom.padB);
                return (
                  <line
                    key={`gy-${i}`}
                    x1={geom.padL}
                    x2={geom.width - geom.padR}
                    y1={y}
                    y2={y}
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="0.18"
                    strokeDasharray="0.4 0.6"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}

              {/* Eje Y: valores (right-aligned al margen izquierdo del plot) */}
              {yTicks.map((v, i) => {
                const yRatio = (v - geom.yMin) / (geom.yMax - geom.yMin);
                const y =
                  geom.padT + (1 - yRatio) * (geom.height - geom.padT - geom.padB);
                return (
                  <text
                    key={`yl-${i}`}
                    x={geom.padL - 0.8}
                    y={y + 0.8}
                    textAnchor="end"
                    fontSize="2.4"
                    fill="rgba(245,237,222,0.5)"
                    fontFamily="ui-monospace, SFMono-Regular, monospace"
                  >
                    {formatPriceES(v).replace(' €', '')}
                  </text>
                );
              })}

              {/* Eje X: 3 fechas */}
              {xTicks.map((tk, i) => (
                <text
                  key={`xl-${i}`}
                  x={tk.x}
                  y={geom.height - geom.padB + 4}
                  textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
                  fontSize="2.4"
                  fill="rgba(245,237,222,0.5)"
                >
                  {tk.label}
                </text>
              ))}

              {/* Área bajo la curva */}
              <polygon points={areaPath} fill="url(#priceArea)" />

              {/* Línea principal */}
              <path
                d={linePath}
                fill="none"
                stroke="url(#priceLine)"
                strokeWidth="0.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />

              {/* Marcador del último punto */}
              {points.length > 0 && (
                <circle
                  cx={geom.xs[geom.xs.length - 1]}
                  cy={geom.ys[geom.ys.length - 1]}
                  r="0.9"
                  fill="#FFD86B"
                  stroke="#1a1d2e"
                  strokeWidth="0.25"
                />
              )}

              {/* Línea vertical + dot del hover */}
              {hover != null && geom.xs[hover] != null && (
                <>
                  <line
                    x1={geom.xs[hover]}
                    x2={geom.xs[hover]}
                    y1={geom.padT}
                    y2={geom.height - geom.padB}
                    stroke="rgba(255, 216, 107, 0.45)"
                    strokeWidth="0.2"
                    vectorEffect="non-scaling-stroke"
                  />
                  <circle
                    cx={geom.xs[hover]}
                    cy={geom.ys[hover]}
                    r="0.95"
                    fill="#FFD86B"
                    stroke="#0b0d18"
                    strokeWidth="0.3"
                  />
                </>
              )}
            </svg>

            {/* Tooltip flotante (DOM, no SVG, para texto nítido) */}
            {hover != null && points[hover] && (
              <Tooltip
                point={points[hover]!}
                xRatio={geom.xs[hover]! / geom.width}
                granularity={granularity}
              />
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function labelForPoint(p: SerialPoint, range: Range, gran: Granularity): string {
  if (gran === 'intraday') return formatTimeMadrid(p.t);
  return formatDateAxis(p.iso, range === '1y');
}

function labelForPointDetailed(p: SerialPoint, gran: Granularity): string {
  if (gran === 'intraday') {
    // ej: "1 may 14:30"
    const d = new Date(p.t);
    const day = d.getUTCDate();
    const month = MONTHS_ES_SHORT[d.getUTCMonth()];
    return `${day} ${month} ${formatTimeMadrid(p.t)}`;
  }
  return formatDateLong(p.iso);
}

function Tooltip({
  point,
  xRatio,
  granularity,
}: {
  point: SerialPoint;
  xRatio: number;
  granularity: Granularity;
}) {
  // Posición horizontal en %, evita salirse por la derecha.
  const leftPct = Math.max(4, Math.min(96, xRatio * 100));
  const flip = leftPct > 70;
  return (
    <div
      className="pointer-events-none absolute -translate-x-1/2 rounded-md border border-gold-500/30 bg-navy-950/95 px-2.5 py-1.5 text-xs shadow-lg backdrop-blur"
      style={{
        left: `${leftPct}%`,
        top: '4%',
        transform: flip ? 'translateX(-95%)' : 'translateX(-5%)',
      }}
    >
      <div className="font-mono text-gold-300 text-sm leading-tight">
        {formatPriceES(point.eurPerGram)}
        <span className="ml-1 text-[10px] text-cream/55">/ g</span>
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-cream/55">
        {labelForPointDetailed(point, granularity)}
      </div>
    </div>
  );
}
